import { createSlice } from '@reduxjs/toolkit';
import { fetchWebhookHistory, fetchWebhookHistorySellers } from './webhookHistoryThunks';

/**
 * The staff's history of webhook dispatches (`DeliveryWebhook` rows).
 *
 * The filters are not persisted on purpose: a date range or a seller left
 * behind from last week is the kind of filter nobody remembers setting, and
 * this screen is opened to answer "did the webhook go out?" right now.
 */
export const initialFilters = {
  /** 'all' | 'admin' | 'seller' — see `SCOPE_OPTIONS`. */
  scope: 'all',
  adminId: 'all',
  sellerId: 'all',
  start_date: '',
  end_date: '',
  status: 'all',
  substatus: 'all',
  dispatch_state: 'all',
  search: '',
  page: 1,
  rowsPerPage: 20,
};

const initialState = {
  rows: [],
  totalCount: 0,
  /** The listing is the unfiltered default, cut to the newest `defaultLimit` rows. */
  capped: false,
  defaultLimit: null,
  loading: false,
  error: null,
  filters: initialFilters,
  /** `[{ value, label, email, adminId }]`, every seller of the platform. */
  sellers: [],
  sellersLoaded: false,
};

const webhookHistorySlice = createSlice({
  name: 'webhookHistory',
  initialState,
  reducers: {
    /** Any filter but the page: changing one starts the listing over. */
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      state.filters.page = 1;
      // A substatus belongs to a status: changing the status drops it.
      if (key === 'status') state.filters.substatus = 'all';
      // A scope only keeps the id that belongs to it.
      if (key === 'scope') {
        if (value !== 'admin') state.filters.adminId = 'all';
        if (value !== 'seller') state.filters.sellerId = 'all';
      }
    },
    setDateRange: (state, action) => {
      state.filters.start_date = action.payload.start_date || '';
      state.filters.end_date = action.payload.end_date || '';
      state.filters.page = 1;
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1;
    },
    resetFilters: (state) => {
      state.filters = { ...initialFilters, rowsPerPage: state.filters.rowsPerPage };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWebhookHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebhookHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload.results;
        state.totalCount = action.payload.count;
        state.capped = action.payload.capped;
        state.defaultLimit = action.payload.defaultLimit;
      })
      .addCase(fetchWebhookHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWebhookHistorySellers.fulfilled, (state, action) => {
        state.sellers = action.payload;
        state.sellersLoaded = true;
      });
  },
});

export const { setFilter, setDateRange, setPage, setRowsPerPage, resetFilters } =
  webhookHistorySlice.actions;

export default webhookHistorySlice.reducer;
